// WWI wireless station — the field signals post. A small field-grey hut under
// a banner-red roof (special-service coding) with the iconic oversized feature
// read from any angle: a tall tapering lattice radio mast with three crossarms
// and a red pennant at the tip. The operator works an outdoor desk with a
// spark set on open ground beside the hut.
import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const { box, cyl, gable, poles, slab, ROOF_TRADE } = styleV3;

// Muted WWI materials.
const FIELD_GREY = "#6d6f64";
const GUNMETAL = "#4a4d52";
const TIMBER_W = "#8b6438";
const MUD = "#5d4a33";
const CANVAS_W = "#cbbd93";
const RED = "#b03a3a";

const BASE = 0.12;                     // foundation slab top
const HX = -0.14, HW = 0.5, HD = 0.46; // the hut, west half of the plot
const WALL_H = 0.42;
const MAST_X = 0.28, MAST_Z = -0.24;   // the mast, back-east corner
const DESK_X = 0.22, DESK_Z = 0.24;    // the outdoor operating desk

// The field-grey hut walls, `h` tall on the foundation.
function hut(h: number): Part {
  return box(HW, h, HD, FIELD_GREY, [HX, BASE + h / 2, 0]);
}

// The lattice radio mast: tapering trunk, three crossarms, tip and pennant.
function mast(h: number): Part[] {
  const parts: Part[] = [
    cyl(0.02, 0.05, h, 4, GUNMETAL, [MAST_X, BASE + h / 2, MAST_Z]),
  ];
  const arms = Math.min(3, Math.max(1, Math.round(h / 0.45)));
  for (let i = 0; i < arms; i++) {
    const y = BASE + 0.35 + i * 0.42;
    const w = 0.3 - i * 0.07;
    parts.push(box(w, 0.025, 0.025, GUNMETAL, [MAST_X, y, MAST_Z]));
  }
  return parts;
}

// The mast top: a short tip spike and the banner-red pennant.
function mastTop(h: number): Part[] {
  return [
    cyl(0.008, 0.014, 0.22, 4, GUNMETAL, [MAST_X, BASE + h + 0.11, MAST_Z]),
    box(0.14, 0.07, 0.015, RED, [MAST_X + 0.08, BASE + h + 0.16, MAST_Z]),
  ];
}

// The outdoor operating desk: trestle table, spark set, battery box.
function desk(): Part[] {
  return [
    box(0.34, 0.04, 0.2, TIMBER_W, [DESK_X, BASE + 0.26, DESK_Z]),
    box(0.05, 0.24, 0.16, MUD, [DESK_X - 0.12, BASE + 0.12, DESK_Z]),
    box(0.05, 0.24, 0.16, MUD, [DESK_X + 0.12, BASE + 0.12, DESK_Z]),
    // The wireless set: gunmetal case with a red dial.
    box(0.14, 0.1, 0.1, GUNMETAL, [DESK_X - 0.05, BASE + 0.33, DESK_Z - 0.02]),
    box(0.03, 0.03, 0.02, RED, [DESK_X - 0.05, BASE + 0.34, DESK_Z + 0.04]),
    // Battery crate under the table.
    box(0.12, 0.1, 0.1, TIMBER_W, [DESK_X + 0.04, BASE + 0.05, DESK_Z - 0.02]),
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.86, 0.86)];
  if (s === 1) return [slab(0.86, 0.86), hut(0.2), ...mast(0.5), ...poles(0.8, 0.76, 0.6)];
  if (s === 2) return [slab(0.86, 0.86), hut(WALL_H), ...mast(0.9), ...poles(0.8, 0.76, 1.1)];

  const eave = BASE + WALL_H;
  const MAST_H = 1.45;
  return [
    slab(0.86, 0.86),
    hut(WALL_H),
    // Banner-red gable — the special-service code, ridge along z. ONE part.
    gable(HW + 0.1, HD + 0.1, 0.24, ROOF_TRADE, HX, eave, 0, "z"),
    // The hut door and a small window on the yard side.
    box(0.16, 0.3, 0.04, MUD, [HX, BASE + 0.15, HD / 2 + 0.01]),
    box(0.1, 0.1, 0.03, CANVAS_W, [HX + HW / 2 + 0.01, BASE + 0.28, -0.08]),
    // The ICONIC lattice mast with its guy stake and red pennant.
    ...mast(MAST_H),
    ...mastTop(MAST_H),
    cyl(0.015, 0.02, 0.1, 4, GUNMETAL, [0.36, BASE + 0.05, 0.05]),
    // The outdoor operating desk on open ground.
    ...desk(),
  ];
}

export const wirelessStation = defineModel({
  id: "wireless-station",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  name: "Wireless Station",
  tags: ["building", "wwi", "military", "wireless", "signals", "radio", "communication"],
  summary: "A field-grey signals hut under a tall lattice radio mast.",
  description:
    "A small field signals hut with a banner-red roof and an oversized " +
    "tapering radio mast with crossarms and a red pennant. The operator keys " +
    "the spark set at an outdoor desk on open ground.",
  author: "Fable 5",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => partsFor(0) },
    { id: "lower", label: "Lower", parts: () => partsFor(1) },
    { id: "upper", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "operate", position: [DESK_X, 0.12, DESK_Z + 0.24], facing: Math.PI, description: "The operator's spot at the outdoor desk." },
  ],
  interactions: [
    { id: "operate-set", label: "Keying the wireless", station: "operate", variants: ["complete"], ...benchWork() },
  ],
});
